/**
 * StatsCarousel — the About-panel stats as an animated, swipeable carousel.
 *
 * One stat per slide. When a slide becomes active its data "builds up": donut
 * sweeps in (conic mask, see .pb-donut-reveal in globals.css), bars/columns grow
 * from zero with a stagger, and numbers count up. Auto-advances, pauses on hover/
 * drag, and supports arrows, dots, and swipe (via Embla). Honors prefers-reduced-
 * motion — final state shown instantly, no autoplay.
 *
 * Self-contained halftone rendering (same look as PromptThemeChart) so the
 * animation can be driven per-slide.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type Item = { label: string; count: number };
type Fact = { value: number | string; label: string };
export type StatSlide =
  | { type: 'donut' | 'columns' | 'bars'; title: string; items: Item[] }
  | { type: 'facts'; title: string; facts: Fact[]; longest?: string; shortest?: string };

const MONO = "'FK Grotesk Mono', monospace";
const DISPLAY = "'Delcan Mono', monospace";
const OUTLINE = '#555555';
const PATTERNS = [
  { tile: 6, r: 2.3 },
  { tile: 6, r: 1.85 },
  { tile: 7, r: 1.85 },
  { tile: 7, r: 1.45 },
  { tile: 8, r: 1.35 },
  { tile: 8, r: 1.0 },
];

function maskFor(p: { tile: number; r: number }) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${p.tile}' height='${p.tile}'><circle cx='${p.tile / 2}' cy='${p.tile / 2}' r='${p.r}' fill='black'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
function maskStyle(p: { tile: number; r: number }): React.CSSProperties {
  return {
    backgroundColor: 'currentColor',
    WebkitMaskImage: maskFor(p),
    maskImage: maskFor(p),
    WebkitMaskRepeat: 'repeat',
    maskRepeat: 'repeat',
    WebkitMaskSize: `${p.tile}px ${p.tile}px`,
    maskSize: `${p.tile}px ${p.tile}px`,
  };
}
function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(m.matches);
    const h = () => setReduce(m.matches);
    m.addEventListener('change', h);
    return () => m.removeEventListener('change', h);
  }, []);
  return reduce;
}

/** False until a frame after `active` is true — so CSS transitions actually run. */
function useEntered(active: boolean, reduce: boolean) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (reduce) {
      setEntered(true);
      return;
    }
    if (active) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
  }, [active, reduce]);
  return reduce ? true : entered;
}

function CountUp({ value, run }: { value: number; run: boolean }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!run) {
      setN(0);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 850);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, value]);
  return <>{n.toLocaleString()}</>;
}

function AnimatedColumns({ items, active, reduce }: { items: Item[]; active: boolean; reduce: boolean }) {
  const entered = useEntered(active, reduce);
  const max = Math.max(...items.map((i) => i.count), 1);
  const H = 230;
  const pat = PATTERNS[2];
  return (
    <div className="flex items-end gap-3 sm:gap-7 w-full text-foreground" style={{ height: H + 70 }}>
      {items.map((it, i) => {
        const h = Math.max(10, Math.round((it.count / max) * H));
        return (
          <div key={it.label} className="flex-1 flex flex-col items-center justify-end h-full">
            <span className="tabular-nums mb-2" style={{ fontFamily: DISPLAY, fontSize: '1.1rem' }}>
              <CountUp value={it.count} run={entered} />
            </span>
            <div
              className="w-full"
              style={{
                height: entered ? h : 0,
                maxWidth: 120,
                border: `2px solid ${OUTLINE}`,
                boxSizing: 'border-box',
                transition: reduce ? 'none' : `height .9s cubic-bezier(.2,.8,.2,1) ${i * 60}ms`,
              }}
            >
              <div style={{ width: '100%', height: '100%', ...maskStyle(pat) }} />
            </div>
            <span
              className="text-muted-foreground uppercase mt-3 text-center"
              style={{ fontFamily: MONO, fontSize: '8.5pt', letterSpacing: '0.02em' }}
            >
              {it.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function AnimatedBars({ items, active, reduce }: { items: Item[]; active: boolean; reduce: boolean }) {
  const entered = useEntered(active, reduce);
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="flex flex-col gap-4 w-full text-foreground">
      {items.map((t, i) => (
        <div key={t.label} className="flex items-center gap-4">
          <span
            className="shrink-0 text-muted-foreground uppercase truncate"
            style={{ width: 150, fontFamily: MONO, fontSize: '9.5pt', letterSpacing: '0.02em' }}
          >
            {t.label}
          </span>
          <div className="flex-1" style={{ height: 22 }}>
            <div
              style={{
                height: '100%',
                width: entered ? `${Math.max(6, (t.count / max) * 100)}%` : '0%',
                border: `2px solid ${OUTLINE}`,
                boxSizing: 'border-box',
                transition: reduce ? 'none' : `width .9s cubic-bezier(.2,.8,.2,1) ${i * 55}ms`,
              }}
            >
              <div style={{ width: '100%', height: '100%', ...maskStyle(PATTERNS[i % PATTERNS.length]) }} />
            </div>
          </div>
          <span className="shrink-0 tabular-nums text-right" style={{ width: 44, fontFamily: DISPLAY, fontSize: '1.2rem' }}>
            <CountUp value={t.count} run={entered} />
          </span>
        </div>
      ))}
    </div>
  );
}

function AnimatedDonut({ items, active, reduce }: { items: Item[]; active: boolean; reduce: boolean }) {
  const entered = useEntered(active, reduce);
  const sum = items.reduce((s, t) => s + t.count, 0) || 1;
  const C = 110;
  const R = 104;
  const RI = 60;
  let acc = 0;
  const slices = items.map((t, i) => {
    const start = (acc / sum) * 360;
    acc += t.count;
    const end = (acc / sum) * 360;
    const [x1o, y1o] = polar(C, C, R, start);
    const [x2o, y2o] = polar(C, C, R, end);
    const [x2i, y2i] = polar(C, C, RI, end);
    const [x1i, y1i] = polar(C, C, RI, start);
    const large = end - start > 180 ? 1 : 0;
    const d = `M ${x1o} ${y1o} A ${R} ${R} 0 ${large} 1 ${x2o} ${y2o} L ${x2i} ${y2i} A ${RI} ${RI} 0 ${large} 0 ${x1i} ${y1i} Z`;
    return { label: t.label, d, pat: PATTERNS[i % PATTERNS.length] };
  });
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-8 sm:gap-12 w-full text-foreground">
      <div className={`shrink-0 mx-auto sm:mx-0 ${entered ? 'is-in' : ''} pb-donut-reveal`}>
        <svg viewBox="0 0 220 220" className="w-[240px] h-[240px] sm:w-[300px] sm:h-[300px]" role="img">
          <defs>
            {PATTERNS.map((p, idx) => (
              <pattern key={idx} id={`sc-ht${idx}`} width={p.tile} height={p.tile} patternUnits="userSpaceOnUse">
                <circle cx={p.tile / 2} cy={p.tile / 2} r={p.r} fill="currentColor" />
              </pattern>
            ))}
          </defs>
          {slices.map((s) => (
            <path key={s.label} d={s.d} fill={`url(#sc-ht${PATTERNS.indexOf(s.pat)})`} style={{ stroke: OUTLINE }} strokeWidth={2.5} />
          ))}
        </svg>
      </div>
      <div className="flex-1 w-full flex flex-col gap-3.5">
        {items.map((t, i) => (
          <div key={t.label} className="flex items-center gap-3">
            <div
              className="shrink-0 h-4 w-4 rounded-[3px]"
              style={{ border: `1.5px solid ${OUTLINE}`, boxSizing: 'border-box' }}
            >
              <div style={{ width: '100%', height: '100%', ...maskStyle(PATTERNS[i % PATTERNS.length]) }} />
            </div>
            <span
              className="flex-1 text-muted-foreground uppercase truncate"
              style={{ fontFamily: MONO, fontSize: '10.5pt', letterSpacing: '0.02em' }}
            >
              {t.label}
            </span>
            <span className="tabular-nums" style={{ fontFamily: DISPLAY, fontSize: '1.25rem' }}>
              <CountUp value={t.count} run={entered} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnimatedFacts({
  facts,
  longest,
  shortest,
  active,
  reduce,
}: {
  facts: Fact[];
  longest?: string;
  shortest?: string;
  active: boolean;
  reduce: boolean;
}) {
  const entered = useEntered(active, reduce);
  return (
    <div className="w-full text-foreground">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-7">
        {facts.map((f) => (
          <div key={f.label} className="flex flex-col">
            <span className="tabular-nums" style={{ fontFamily: DISPLAY, fontSize: '1.6rem', lineHeight: 1 }}>
              {typeof f.value === 'number' ? <CountUp value={f.value} run={entered} /> : f.value}
            </span>
            <span
              className="text-muted-foreground uppercase tracking-widest mt-1.5"
              style={{ fontFamily: MONO, fontSize: '8.5pt' }}
            >
              {f.label}
            </span>
          </div>
        ))}
      </div>
      {(longest || shortest) && (
        <div className="mt-8 space-y-3" style={{ fontFamily: "'Martina Plantijn', Georgia, serif" }}>
          {longest && (
            <p className="text-muted-foreground text-sm leading-relaxed">
              <span className="uppercase tracking-widest" style={{ fontFamily: MONO, fontSize: '8.5pt' }}>
                Longest prompt —{' '}
              </span>
              <span className="text-foreground">“{longest.length > 120 ? longest.slice(0, 120) + '…' : longest}”</span>
            </p>
          )}
          {shortest && (
            <p className="text-muted-foreground text-sm leading-relaxed">
              <span className="uppercase tracking-widest" style={{ fontFamily: MONO, fontSize: '8.5pt' }}>
                Shortest prompt —{' '}
              </span>
              <span className="text-foreground">“{shortest}”</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function renderSlide(s: StatSlide, active: boolean, reduce: boolean) {
  if (s.type === 'facts')
    return <AnimatedFacts facts={s.facts} longest={s.longest} shortest={s.shortest} active={active} reduce={reduce} />;
  if (s.type === 'donut') return <AnimatedDonut items={s.items} active={active} reduce={reduce} />;
  if (s.type === 'bars') return <AnimatedBars items={s.items} active={active} reduce={reduce} />;
  return <AnimatedColumns items={s.items} active={active} reduce={reduce} />;
}

export function StatsCarousel({ slides }: { slides: StatSlide[] }) {
  const reduce = usePrefersReducedMotion();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);
  const timerRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);
  const play = useCallback(() => {
    if (reduce || !emblaApi) return;
    stop();
    timerRef.current = window.setInterval(() => emblaApi.scrollNext(), 4800);
  }, [emblaApi, reduce, stop]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    const onReInit = () => {
      setSnaps(emblaApi.scrollSnapList());
      onSelect();
    };
    onReInit();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);
    emblaApi.on('pointerDown', stop);
    emblaApi.on('select', play);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
      emblaApi.off('pointerDown', stop);
      emblaApi.off('select', play);
    };
  }, [emblaApi, stop, play]);

  useEffect(() => {
    play();
    return stop;
  }, [play, stop]);

  if (!slides.length) return null;

  return (
    <div className="mt-16 relative" onMouseEnter={stop} onMouseLeave={play}>
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {slides.map((s, i) => (
            <div key={i} className="flex-[0_0_100%] min-w-0 px-1">
              <p className="mb-8 text-foreground" style={{ fontSize: '1.15rem', fontFamily: DISPLAY }}>
                {s.title}
              </p>
              <div className="min-h-[330px] flex items-center">{renderSlide(s, i === selected, reduce)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls live below the chart — arrows flank the dots so nothing overlaps a graph. */}
      <div className="flex items-center justify-center gap-5 mt-12">
        <button
          onClick={() => emblaApi?.scrollPrev()}
          aria-label="Previous stat"
          className="shrink-0 w-9 h-9 rounded-full border border-border text-foreground flex items-center justify-center hover:bg-accent transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex justify-center gap-2">
          {snaps.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi?.scrollTo(i)}
              aria-label={`Go to stat ${i + 1}`}
              className="h-[7px] rounded-full transition-all duration-300"
              style={{
                width: i === selected ? 22 : 7,
                backgroundColor: i === selected ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground) / 0.4)',
              }}
            />
          ))}
        </div>
        <button
          onClick={() => emblaApi?.scrollNext()}
          aria-label="Next stat"
          className="shrink-0 w-9 h-9 rounded-full border border-border text-foreground flex items-center justify-center hover:bg-accent transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
